import { getApiUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import { Client } from '../types';
import { AnimatedModal } from '@/components/ui/AnimatedModal';
import { readAuthToken } from '@/utils/authToken';

interface EditClientModalProps {
  isOpen: boolean;
  client: Client | null;
  onClose: () => void;
  onClientUpdated: (client: Client) => void;
  onClientDeleted: (clientId: string) => void;
}

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  // Format based on length
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

// Validate email format
const isValidEmail = (email: string): boolean => {
  if (!email) return true; // Empty is valid (optional field)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const EditClientModal: React.FC<EditClientModalProps> = ({
  isOpen,
  client,
  onClose,
  onClientUpdated,
  onClientDeleted
}) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    email: '',
    phone: '',
    address: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showDeleteSection, setShowDeleteSection] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (client && isOpen) {
      setFormData({
        name: client.name || '',
        code: client.code || '',
        email: client.email || '',
        phone: client.phone ? formatPhoneNumber(client.phone) : '',
        address: client.address || ''
      });
      setShowDeleteSection(false);
      setDeleteConfirmation('');
      setEmailError('');
    }
  }, [client, isOpen]);

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setFormData({ ...formData, phone: formatted });
  };

  const handleEmailChange = (value: string) => {
    setFormData({ ...formData, email: value });
    if (value && !isValidEmail(value)) {
      setEmailError('Invalid email format');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Client name is required');
      return;
    }

    if (!client) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(getApiUrl(`/api/clients/${client.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${readAuthToken() || ''}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update client');
      }

      const updatedClient = await response.json();
      onClientUpdated(updatedClient);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update client');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = () => {
    if (!client) return;

    // Check if confirmation text matches
    if (deleteConfirmation.toLowerCase() !== client.name.toLowerCase()) {
      setError(`Please type "${client.name}" exactly to confirm deletion`);
      return;
    }

    setError('');
    performDelete();
  };

  const performDelete = async () => {
    if (!client) return;

    try {
      const response = await fetch(getApiUrl(`/api/clients/${client.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${readAuthToken() || ''}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete client');
      }

      onClientDeleted(client.id);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete client');
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting && !isDeleting) {
      // Reset state
      setFormData({
        name: '',
        code: '',
        email: '',
        phone: '',
        address: ''
      });
      setError('');
      setShowDeleteSection(false);
      setDeleteConfirmation('');
      setIsDeleting(false);
      onClose();
    }
  };

  if (!client) return null;

  return (
    <AnimatedModal isOpen={isOpen} onClose={handleClose} size="sm" disableBackdropClose={isSubmitting || isDeleting} className="max-h-[90vh] overflow-y-auto">
      <div className="p-4">
        {/* Compact Header */}
        <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Edit Client</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            disabled={isSubmitting || isDeleting}
          >
            <i className="fas fa-times text-sm"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs flex items-center gap-2">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}

          <div className="space-y-3">
            {/* Name and Code in same row */}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-0.5">
                  Client Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-8 px-2.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Client name"
                  disabled={isSubmitting || isDeleting}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-0.5">Code</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="w-full h-8 px-2.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 uppercase"
                  placeholder="ALTRU"
                  maxLength={10}
                  disabled={isSubmitting || isDeleting}
                />
              </div>
            </div>

            {/* Email and Phone in same row */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-0.5">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  className={`w-full h-8 px-2.5 text-sm border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    emailError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="contact@client.com"
                  disabled={isSubmitting || isDeleting}
                />
                {emailError && <p className="text-xs text-red-500 mt-0.5">{emailError}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-0.5">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  className="w-full h-8 px-2.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="(123) 456-7890"
                  maxLength={14}
                  disabled={isSubmitting || isDeleting}
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-0.5">Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full h-8 px-2.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="123 Main St, City, State 12345"
                disabled={isSubmitting || isDeleting}
              />
            </div>
          </div>

          {/* Compact Footer */}
          <div className="mt-4 pt-3 border-t border-gray-100 flex gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 h-8 px-3 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50"
              disabled={isSubmitting || isDeleting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 h-8 px-3 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
              disabled={isSubmitting || isDeleting || !!emailError}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-circle-notch fa-spin"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-check"></i>
                  Save
                </>
              )}
            </button>
          </div>
        </form>

        {/* Delete Section - Compact */}
        <div className="mt-4 pt-3 border-t border-gray-100">
          {!showDeleteSection ? (
            <button
              onClick={() => setShowDeleteSection(true)}
              className="text-xs text-red-500 hover:text-red-700"
              disabled={isSubmitting || isDeleting}
            >
              <i className="fas fa-trash-alt mr-1.5"></i>
              Delete Client
            </button>
          ) : (
            <div className="space-y-3">
              <div className="bg-red-50 border border-red-200 rounded p-2 flex items-start gap-2">
                <i className="fas fa-exclamation-triangle text-red-500 text-sm mt-0.5"></i>
                <div className="text-xs text-red-700">
                  <p className="font-medium">This will permanently delete all client data, deposits, and history.</p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-0.5">
                  Type <span className="font-bold text-red-600">"{client.name}"</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  className="w-full h-8 px-2.5 text-sm border border-red-300 rounded focus:ring-1 focus:ring-red-500"
                  placeholder={client.name}
                  disabled={isDeleting}
                />
              </div>

              <button
                onClick={handleDeleteClick}
                className={`w-full h-9 rounded text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                  isDeleting
                    ? 'bg-red-600 text-white cursor-wait'
                    : deleteConfirmation.toLowerCase() === client.name.toLowerCase()
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                disabled={deleteConfirmation.toLowerCase() !== client.name.toLowerCase() || isDeleting}
              >
                {isDeleting ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin"></i>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash-alt"></i>
                    Delete Client
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  setShowDeleteSection(false);
                  setDeleteConfirmation('');
                  setError('');
                }}
                className="w-full text-xs text-gray-500 hover:text-gray-700"
                disabled={isDeleting}
              >
                Cancel Deletion
              </button>
            </div>
          )}
        </div>
      </div>
    </AnimatedModal>
  );
};